<script setup>
import { computed, onMounted, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { Banners, ButtonComponent } from '@/components';
import { useScreenSize } from '@/composables/useScreenSize';
import ComplianceConnectEld from '@/components/Compliance/ComplianceConnectEld.vue';
import CertificateOfInsurance from '@/components/Compliance/CertificateOfInsurance.vue';
import FileInsuranceInformationModal from '@/components/modals/FileInsuranceInformationModal.vue';
import CargoPolicy from '@/components/Compliance/CargoPolicy.vue';
import CollapsiblePanel from '@/components/CollapsiblePanel.vue';
import EldSectionMessages from '@/components/EldSectionMessages.vue';
import CarrierPortalEldProviderModal from '@/components/modals/CarrierPortalEldProviderModal.vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';

const toast = useToast();
const router = useRouter();
const store = useStore();
const { screenSize } = useScreenSize();

const myComplianceTabs = {
  eld: { title: 'Vehicles' },
  insurance: { title: 'Insurance' },
};
const pageTabs = ref(myComplianceTabs);
const activePageTab = computed(
  () => router.currentRoute.value.query.tab || Object.keys(pageTabs.value)[0]
);

const loading = ref(true);

const carrier_id = computed(
  () => store.state.users?.currentUser?.company?.carrier_id
);
const company_id = computed(() => store.state.users?.currentUser?.company?.id);

const bannerType = computed(
  () => store.state.myLoadsStore.equipmentTableVehicles?.eldStatus
);

const displayExclusionsSection = computed(() => {
  return store.state.users.featureFlags?.display_exclusions_section?.enabled;
});

const displayEquipmentBanner = computed(() => {
  return store.state.users.featureFlags?.display_equipment_banner?.enabled;
});

onMounted(async () => {
  loading.value = true;

  await Promise.all([
    store.dispatch('onboardingPacket/getEldIntegrations', company_id.value),
    store.dispatch('onboardingPacket/getLatestEldProvider', carrier_id.value),
    store.dispatch(
      'onboardingPacket/getEldVehichleInformation',
      carrier_id.value
    ),
    store.dispatch('insurance/getPolicyByCarrier', carrier_id.value),
    store.dispatch('myLoadsStore/getEquipmentTableCarrierVehicles'),
  ]);

  const res = await store.dispatch('onboardingPacket/getEldStatus');
  if ([200, 201, 304].includes(res?.status) && res?.data?.eld_status) {
    store.commit('onboardingPacket/setEldStatus', res.data);
  }
  loading.value = false;
});
onBeforeUnmount(() => {
  window.onscroll = null;
});

// Using the loadboard access endpoint since it provides all required data points for this page.
const generalStatus = computed(
  () => store.state.users.userLoadBoardAccess?.user
);

const isTabletOrMobile = computed(
  () => screenSize.value === 'tablet' || screenSize.value === 'mobile'
);

const isMobile = computed(() => screenSize.value === 'mobile');

const setActivePageTab = (tab) => {
  router.push({
    query: { ...router.currentRoute.value.query, tab: tab },
  });
};

// if no tab add the default tab option
if (!router.currentRoute.value.query?.tab) {
  router.replace({
    query: {
      ...router.currentRoute.value.query,
      tab: Object.keys(pageTabs.value)[0],
    },
  });
}

const handleConnectEldClick = () => {
  const dialog = document.getElementById('dialog');
  dialog?.showModal();
};

const isInsuranceModalVisible = ref(false);

const handleInsuranceCertificateRequest = async (agency) => {
  const { name, email, phoneNumber } = agency;

  const payload = {
    carrier_id: store.state.users?.currentUser?.company?.carrier_id,
    contact_email: email,
    contact_phone: phoneNumber,
    contact_name: name,
    company_ids: [1],
    is_type: 'highway',
  };

  try {
    const res = await store.dispatch(
      'insurance/insuranceCertificateRequest',
      payload
    );

    if ([200, 201, 204].includes(res.status)) {
      await store.dispatch('users/checkLoadBoardAccess');
      toast?.info('Request sent', {
        position: 'top-right',
        timeout: 5000,
      });
    }
  } catch (error) {
    console.error('Error sending insurance request:', error);
    toast?.error('An error occurred. Please try again.', {
      position: 'top-right',
      timeout: 5000,
    });
  } finally {
    isInsuranceModalVisible.value = false;
  }
};

const handleRefreshInsuranceClick = () => {
  toast?.open({
    message: 'Refreshing policy',
    position: 'top-right',
    duration: 5000,
  });
  store.dispatch('insurance/getPolicyByCarrier', carrier_id.value);
};

const hasEldProvider = computed(() =>
  store.state.onboardingPacket?.latest_carrier_eld_provider?.connect_channel?.id
    ? true
    : false
);

const hasVehicles = computed(
  () => store.state.myLoadsStore.equipmentTableVehicles.vehicles.length > 0
);
</script>

<template>
  <Banners
    :is-ultra="true"
    :sticky-top="true"
  >
    <div class="container-wide-eq mx-auto flex flex-row gap-5 px-6">
      <div
        class="flex w-full flex-col py-4 pt-10 text-left"
        :style="isTabletOrMobile ? 'height: calc(100dvh - 102px)' : ''"
      >
        <div class="flex flex-col gap-2 text-left">
          <div class="mr-4 flex w-full items-center justify-between gap-4">
            <h1
              class="font-medium text-steel-1100"
              :class="isMobile ? 'text-xl' : 'text-3xl'"
            >
              Equipment
            </h1>
            <ButtonComponent
              v-if="activePageTab === 'eld' && !loading && hasEldProvider"
              class="text-xs md:text-sm"
              @click="handleConnectEldClick"
            >
              New ELD Connection
            </ButtonComponent>
          </div>
          <EldSectionMessages
            v-if="!loading && displayEquipmentBanner"
            :status="bannerType"
            :width="'p-3'"
            @submit-coi-request="isInsuranceModalVisible = true"
            @update-eld-account="handleConnectEldClick"
          />
          <p class="max-w-[800px] text-xs font-normal text-steel-800">
            Highway checks your ELD’s VINs and compares them to your Insurance.
            This way, we know which of your vehicles are insured. If a VIN isn’t
            on both the ELD and the Insurance, we’ll show you so that you can
            correct it.
            <a
              href="https://service.highway.com/en/articles/9807670-why-do-i-need-to-provide-the-list-of-vehicles-covered-by-my-insurance-policy"
              target="_blank"
              class="text-sun-700 underline hover:text-sun-800 active:text-sun-900"
            >
              Learn more
            </a>
          </p>

          <div
            v-if="!loading && hasVehicles && !hasEldProvider"
            class="flex items-center justify-between self-stretch rounded-lg bg-white py-2 pl-4 pr-3 outline outline-1 outline-offset-[-1px] outline-steel-200"
          >
            <div class="flex flex-1 flex-col items-start justify-start gap-1">
              <div
                class="justify-start self-stretch font-['Poppins'] text-base font-medium leading-6 tracking-tight text-steel-1100"
              >
                Connect your ELD
              </div>
              <div
                class="justify-start font-['Poppins'] text-sm font-normal leading-5 tracking-tight text-steel-700"
              >
                For the best experience, connect your ELD to verify your
                equipment and build trust and visibility among our community of
                brokers.
              </div>
            </div>
            <div class="flex items-center justify-start gap-4">
              <a
                href="https://service.highway.com/en/articles/9807670-why-do-i-need-to-provide-the-list-of-vehicles-covered-by-my-insurance-policy"
                target="_blank"
                class="flex h-12 min-w-max items-center justify-center !rounded-md bg-steel-100 px-4 py-2 text-sm font-medium text-steel-900 transition-colors duration-100 hover:bg-steel-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-400 active:bg-steel-400 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-steel-100 disabled:active:bg-steel-100 md:h-9 md:text-sm"
              >
                Learn more
              </a>
              <ButtonComponent
                variant="primary"
                @click="handleConnectEldClick"
              >
                Connect ELD
              </ButtonComponent>
            </div>
          </div>
          <div class="mb-2 mt-5 flex w-full items-center justify-between">
            <div
              class="flex w-full items-center pt-2"
              :class="
                isTabletOrMobile
                  ? 'justify-evenly border-b border-steel-300'
                  : 'gap-8'
              "
            >
              <div
                v-for="tab in Object.keys(pageTabs)"
                :key="`${tab}-link`"
                :title="`Change to ${pageTabs[tab]?.title}`"
                class="tab flex cursor-pointer items-center gap-1 whitespace-nowrap rounded leading-tight tracking-tight"
                :class="[
                  {
                    'text-steel-700': activePageTab !== tab,
                    'text-sun-700': activePageTab === tab,
                  },
                  isTabletOrMobile
                    ? 'flex-1 justify-center text-sm font-semibold'
                    : 'text-base',
                ]"
                @click="setActivePageTab(tab)"
              >
                <div
                  class="relative flex w-full flex-col"
                  :class="{ 'pb-2': isTabletOrMobile }"
                >
                  <div class="flex justify-center gap-1">
                    <div>{{ pageTabs[tab]?.title }}</div>
                  </div>
                  <span
                    class="duration-50 absolute bottom-0 left-1/2 w-full -translate-x-1/2 transform rounded bg-sun-700 transition-all"
                    :class="
                      activePageTab === tab
                        ? 'h-[3px] opacity-100'
                        : 'h-[3px] opacity-0'
                    "
                  ></span>
                  <div
                    v-if="!isTabletOrMobile && activePageTab === tab"
                    class="h-0.5 w-full rounded-sm bg-sun-700"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!isTabletOrMobile"
          class="flex flex-col gap-10 text-left"
        >
          <div
            v-if="activePageTab === 'eld'"
            id="eld"
            class="toc-section"
          >
            <ComplianceConnectEld />
          </div>
          <div
            v-if="
              activePageTab === 'insurance' &&
              (generalStatus.has_active_coi_on_file ||
                generalStatus.cargo_policy_on_file_and_active)
            "
            id="insurance"
            class="toc-section mt-2 flex flex-col gap-6"
          >
            <CertificateOfInsurance
              :loading="loading"
              @open-insurance-modal="isInsuranceModalVisible = true"
            />
          </div>
          <CargoPolicy
            v-if="activePageTab === 'insurance' && displayExclusionsSection"
            :loading="loading"
          />
        </div>
        <div v-else>
          <template v-if="activePageTab === 'eld'">
            <ComplianceConnectEld />
          </template>
          <div v-show="activePageTab === 'insurance'">
            <CollapsiblePanel
              title="Certificate of Insurance"
              background-color="bg-white"
            >
              <CertificateOfInsurance
                :loading="loading"
                @open-insurance-modal="isInsuranceModalVisible = true"
                @refresh-insurance="handleRefreshInsuranceClick"
              />
            </CollapsiblePanel>
            <CollapsiblePanel
              title="Exclusions"
              background-color="bg-white"
            >
              <CargoPolicy :loading="loading" />
            </CollapsiblePanel>
          </div>
        </div>
      </div>
    </div>
    <dialog
      id="dialog"
      class="dialog-carrier-dashboard outline-none"
    >
      <CarrierPortalEldProviderModal />
    </dialog>
    <FileInsuranceInformationModal
      v-if="isInsuranceModalVisible"
      :visible="isInsuranceModalVisible"
      @close="isInsuranceModalVisible = false"
      @submit="handleInsuranceCertificateRequest"
    />
  </Banners>
</template>
